import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Select,
  ListItemText,
  SelectChangeEvent,
  OutlinedInput,
  InputLabel,
  FormControl
} from '@mui/material'
import { Box } from '@mui/system'
import {
  FieldPath,
  FormProvider,
  RegisterOptions,
  useForm,
  get,
  Controller
} from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { useParams } from 'react-router'
import { InputCheck, InputField } from '../../../components/FormCommons'
import { useArticle, useArticleApi } from './useArticlesApi'
import { useCallback, useEffect, useState } from 'react'
import {
  buildPhoto,
  UploadablePhoto
} from '../../organizer/products/forms/ProductSectionPhotos'
import { SavingButton } from '../../../components/commons/SavingButton'
import CustomEditor from './CustomEditor'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const languages = ['pl', 'en']

export const ArticlePage = () => {
  const params = useParams<any>()
  const { loading, data, refetch } = useArticle(params.id as string)
  return (
    <>
      {loading && <LinearProgress />}
      {data && (
        <ArticleForm article={data} refetch={refetch} type={data.type} />
      )}
    </>
  )
}

export const ArticleForm = ({
  article,
  refetch,
  type
}: {
  article?: any
  refetch?: Function
  type: any
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [seoCategories, setSeoCategories] = useState([])
  const { updateArticle, createArticle, runCommand, listArticles } =
    useArticleApi()
  const { enqueueSnackbar } = useSnackbar()
  const [hasDeleteAlert, setHasDeleteAlert] = useState(false)
  const [loading, setLoading] = useState(false)
  const [slug, setSlug] = useState('')
  const [tag, setTags] = useState<string[]>([])
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([])

  const handleSupportedLanguages = (event: SelectChangeEvent<typeof supportedLanguages>) => {
    const {
      target: { value }
    } = event
    setSupportedLanguages(typeof value === 'string' ? value.split(',') : value)
  }

  useEffect(() => {
    loadSeoCategories()
  }, [])
  const loadSeoCategories = useCallback(async () => {
    try {
      const result = await listArticles({ type: 'seo' }, false)
      setSeoCategories(result)
    } catch (err) {
      console.error('Erro ', err)
    }
  }, [])

  const [htmlContent, setHtmlContent] = useState(article?.content_text ?? '')
  const [htmlContentEn, setHtmlContentEn] = useState(
    article?.content_text_en ?? ''
  )

  const defaultValues: any = article
    ? {
        ...article,
        type: type
      }
    : {
        type: type
      }
  const formMethods = useForm<any>({
    defaultValues
  })
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = formMethods

  const register = (name: FieldPath<any>, options?: RegisterOptions) => ({
    ...formMethods.register(name, { ...options }),
    id: name,
    error: Boolean(get(errors, name, false))
  })

  const handleDelete = async () => {
    const command = {
      type: 'Article.Delete',
      aggregateId: article.id
    }
    try {
      await runCommand(command)
      enqueueSnackbar('Artykuł usunięty')
      setHasDeleteAlert(false)
      refetch()
      navigate('/super/articles')
    } catch (err) {
      enqueueSnackbar('Problem z usunięciem artykułu')
    }
  }

  async function onFormSubmit (rawData: any) {
    setLoading(true)

    const article = {
      ...rawData,
      tags: tag,
      parent_id: rawData['parent_id'] === '' ? null : rawData['parent_id'],
      content_text: htmlContent,
      slug,
      content_text_en: htmlContentEn,
      supported_languages: formMethods.getValues('supportedLanguages')

    }
    if (article.published_at === '') {
      article.published_at = null
    }

    await (article.id ? updateArticle(article) : createArticle(article))

    enqueueSnackbar('Zapisano', { variant: 'success' })
    setLoading(false)
    navigate('/super/articles')
  }

  const category_id = formMethods.watch('category_id')
  const parent_id = formMethods.watch('parent_id')
  const main_photo = formMethods.watch('main_photo')

  const handleMainPhotoUploaded = (newPhoto: any) => {
    formMethods.setValue('main_photo', buildPhoto(newPhoto))
    return true
  }

  useEffect(() => {
    const langs = article?.supported_languages;
    if (langs && Array.isArray(langs)) {
      formMethods.setValue('supportedLanguages', langs);
    }
  }, [article]);


  return (
    <Box sx={{ maxWidth: 672 }}>
      <h2 className='text-lg p-3 font-bold bg-sky-50 my-5'>
        {type === 'blog' ? 'Artykuł' : 'Seo'}
      </h2>
      <Box>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            {type === 'seo' && (
              <>
                <InputField label='Link' {...register('link')} />
                <InputField
                  label='Umieść pod'
                  {...register('parent_id')}
                  value={parent_id}
                  select
                >
                  <MenuItem value=''>Wybierz nadrzedny wpis</MenuItem>
                  {seoCategories.map(c => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.title}
                    </MenuItem>
                  ))}
                </InputField>
              </>
            )}

            <Controller
              name={'title'}
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => {
                return (
                  <InputField
                    label='Tytuł'
                    {...register('title')}
                    onChange={onChange}
                  />
                )
              }}
            />

            <Controller
              name={'slug'}
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => {
                setSlug(
                  value ? value : ''
                )

                return (
                    <InputField
                        label='Slug (Adres)'
                        {...register('slug')}
                        onChange={onChange}
                    />
                )
              }}
            />

            {type === 'seo' && (
              <>
                <Controller
                    name={'h1_tag'}
                    control={control}
                    rules={{
                      required: true
                    }}
                    render={({ field: { onChange, value } }) => {
                      return (
                          <InputField
                              label='h1 tag'
                              {...register('h1_tag')}
                              onChange={onChange}
                          />
                      )
                    }}
                />

                <InputField
                  label='Meta'
                  {...register('meta')}
                  multiline
                  minRows={4}
                />
                <InputField
                  label='Description'
                  {...register('summary')}
                  multiline
                  minRows={4}
                />
                <InputField
                    label='Position'
                    {...register('position_in_tree', {
                      setValueAs: value => value === '' ? null : parseInt(value, 10)
                    })}
                    type='number'
                />
              </>
            )}

            {type === 'blog' && (
              <>
                <InputField
                  label='Podsumowanie'
                  {...register('summary')}
                  multiline
                  minRows={4}
                />
                <InputField label='Autor' {...register('author_name')} />
                <div className='mb-2'>
                  <div className='h-[288px] w-[672px] bg-gray-100 my-4 '>
                    <UploadablePhoto
                      photo={main_photo}
                      onPhotoUploaded={handleMainPhotoUploaded}
                      label='Dodaj zdjęcie główne'
                    />
                  </div>
                </div>
              </>
            )}
            <CustomEditor
              value={htmlContent}
              onChange={data => {
                setHtmlContent(data)
              }}
            />

            {article?.deleted_at ? (
              <div className='text-center text-xl text-red-500 font-bold'>
                Ten artykuł został usunięty {article.deleted_at}
              </div>
            ) : (
              <Controller
                name={'published_at'}
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error }
                }) => {
                  var val =
                    value != null && value.length > 10
                      ? value.substr(0, 10)
                      : value
                  return (
                    <InputField
                      type='date'
                      onChange={onChange}
                      value={val}
                      InputLabelProps={{ shrink: true }}
                      label='Data publikacji'
                      id={`published_at`}
                    />
                  )
                }}
              />
            )}

            {type === 'seo' && (
              <>
                <div className='w-1/3'>
                  <InputCheck label='Aktywny' {...register('active')} />
                </div>

                <div className='w-1/3'>
                  <InputCheck
                    label='Pokazuj w nawigacji'
                    {...register('nav')}
                  />
                </div>
              </>
            )}
            {type === 'seo' && (
              <>
                <InputField label='Link' {...register('link')} />
                <InputField
                  label='Umieść pod'
                  {...register('parent_id')}
                  value={parent_id}
                  select
                >
                  <MenuItem value=''>Wybierz nadrzedny wpis</MenuItem>
                  {seoCategories.map(c => (
                    <MenuItem value={c.id} key={c.id}>
                      {c.title}
                    </MenuItem>
                  ))}
                </InputField>
              </>
            )}

            <Controller
              name={'title_en'}
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => {
                return (
                  <InputField
                    label='Tytuł angielski'
                    {...register('title_en')}
                    onChange={onChange}
                  />
                )
              }}
            />

            {type === 'seo' && (
                <>
                  <Controller
                      name={'h1_tag_en'}
                      control={control}
                      rules={{
                        required: true
                      }}
                      render={({ field: { onChange, value } }) => {
                        return (
                            <InputField
                                label='h1 tag angielski'
                                {...register('h1_tag_en')}
                                onChange={onChange}
                            />
                        )
                      }}
                  />

                  <InputField
                      label='Meta angielski'
                      {...register('meta_en')}
                      multiline
                      minRows={4}
                  />
                </>
            )}

            <InputField
              label='Podsumowanie angielskie'
              {...register('summary_en')}
              multiline
              minRows={4}
            />
            
            <CustomEditor
              value={htmlContentEn}
              onChange={data => {
                setHtmlContentEn(data)
              }}
            />

            <Controller
                name="supportedLanguages"
                control={control}
                defaultValue={article?.supported_languages || []}
                render={({ field: { onChange, value } }) => (
                    <FormControl sx={{ mt: 4, width: 1 / 3 }}>
                      <InputLabel>{t("SuperAdmin.supported_languages")}</InputLabel>
                      <Select
                          fullWidth
                          multiple
                          value={value}
                          onChange={(e) => {
                            onChange(e.target.value);
                            handleSupportedLanguages(e);
                          }}
                          input={<OutlinedInput label={t("SuperAdmin.supported_languages")} />}
                          renderValue={(selected) => selected.join(', ')}
                      >
                        {languages.map((name) => (
                            <MenuItem key={name} value={name}>
                              <ListItemText primary={name} />
                            </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                )}
            />

            <Box display='flex' my={2}>
              <Box>
                <Button variant='text' color='secondary'>
                  Anuluj
                </Button>
              </Box>
              {article && (
                <div>
                  <Button
                    variant='text'
                    color='secondary'
                    onClick={() => setHasDeleteAlert(true)}
                  >
                    Usuń artykuł
                  </Button>
                </div>
              )}
              <Box flex={1} />
              <SavingButton
                variant='contained'
                type='submit'
                label={
                  loading ? (
                    <CircularProgress size={20} color='info' />
                  ) : (
                    'Zapisz'
                  )
                }
              ></SavingButton>
            </Box>
          </form>
        </FormProvider>
      </Box>

      <Dialog
        open={hasDeleteAlert}
        onClose={() => setHasDeleteAlert(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {'Czy na pewno usunąć artykuł?'}
        </DialogTitle>
        <DialogContent>
          {/* <DialogContentText id="alert-dialog-description">
            Let Google help apps determine location. This means sending
            anonymous location data to Google, even when no apps are running.
          </DialogContentText> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHasDeleteAlert(false)}>Anuluj</Button>
          <Button onClick={handleDelete} autoFocus>
            Tak
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
